"use client";

import { useId, useState } from "react";
import { useTranslations } from "use-intl";

import { SortIcon } from "@/components/appIcons";
import { FacetFilterBarCompact } from "@/components/facets/facet-filter-bar-compact";
import {
  FacetFilterControls,
  MobileFacetFiltersDisclosure,
} from "@/components/facets/facet-filter-controls";
import { FacetFormatControl } from "@/components/facets/facet-format-control";
import { FacetMobileSort } from "@/components/facets/facet-mobile-sort";
import { FacetSearchInput } from "@/components/facets/facet-search-input";
import type { LinkTarget } from "@/components/link";
import { FilterSelect, type FilterOption } from "@/components/ui/filter-select";
import { FilterToolbarControls } from "@/components/ui/filter-toolbar";
import { activeFacetFilterCount } from "@/facets/active-facet-filter-count";
import type { SurfaceConfig } from "@/facets/config";
import type { FacetFiltersController } from "@/facets/use-facet-filters";
import { useFacetSearchInput } from "@/hooks/use-facet-search-input";
import { cn } from "@/lib/utils";

const COMPACT_FORMAT_CLASS = "min-w-full sm:min-w-0";

interface FacetFilterBarProps {
  surface: SurfaceConfig;
  /** Facet options (value/label/count, with optional iconUrl flag) per key. */
  options: Record<string, FilterOption[]>;
  controller: FacetFiltersController;
  /** Free-text search (the separate `q` param) — optional. */
  search?: string | null;
  onSearchChange?: (value: string | null) => void;
  searchPlaceholder?: string;
  formatValue?: string;
  onFormatChange?: (value: string) => void;
  formatTotalCount?: number;
  formatLinkTarget?: (value: string) => LinkTarget;
  collapseFiltersOnMobile?: boolean;
  /**
   * `stacked` (default) gives format, search and facet widgets their own rows.
   * `compact` keeps format, search, a "Filters" popover and sort on one row
   * (two on narrow screens) for pages where the results should start high.
   */
  layout?: "stacked" | "compact";
}

function FacetFilterControlsSection({
  collapsible,
  controller,
  options,
  surface,
}: {
  collapsible: boolean;
  controller: FacetFiltersController;
  options: Record<string, FilterOption[]>;
  surface: SurfaceConfig;
}) {
  const t = useTranslations("facets");
  const [open, setOpen] = useState(false);
  const controlsId = useId();
  const controls = (
    <FilterToolbarControls
      label={t("filterBy")}
      className="sm:w-full sm:flex-nowrap sm:[&>div]:min-w-0 sm:[&>div]:flex-1 sm:[&>div_button]:w-full"
    >
      <FacetFilterControls surface={surface} options={options} controller={controller} />
    </FilterToolbarControls>
  );

  if (!collapsible) return controls;

  return (
    <>
      <MobileFacetFiltersDisclosure
        activeCount={activeFacetFilterCount(surface, controller)}
        controlsId={controlsId}
        open={open}
        onToggle={() => setOpen((current) => !current)}
      />
      <div id={controlsId} className={cn(!open && "hidden", "sm:block")}>
        {controls}
      </div>
    </>
  );
}

/**
 * One config-driven facet bar shared by every faceted surface. Renders `format`
 * as a prominent chip row, the rest as dropdown widgets, a sort select, an
 * optional free-text input, and clear-all. Facet/sort edits flow through the
 * {@link FacetFiltersController} (which writes the unified `s` param); the text
 * input writes the separate `q` param via `onSearchChange`.
 */
export function FacetFilterBar({
  surface,
  options,
  controller,
  search,
  onSearchChange,
  searchPlaceholder,
  formatValue,
  onFormatChange,
  formatTotalCount,
  formatLinkTarget,
  collapseFiltersOnMobile = false,
  layout = "stacked",
}: FacetFilterBarProps) {
  const t = useTranslations("facets");

  const searchInput = useFacetSearchInput(search, onSearchChange);

  const formatDef = surface.facets.find((facet) => facet.key === "format");
  const formatOptions = options.format ?? [];
  const sortOptions = surface.sorts.map((sort) => ({
    value: sort.value,
    label: t(sort.labelKey),
    icon: sort.icon,
  }));
  const hasSort = sortOptions.length > 0;
  const formatSelector = (
    <FacetFormatControl
      className={layout === "compact" ? COMPACT_FORMAT_CLASS : undefined}
      formatAvailable={Boolean(formatDef)}
      formatOptions={formatOptions}
      controller={controller}
      formatValue={formatValue}
      onFormatChange={onFormatChange}
      formatTotalCount={formatTotalCount}
      formatLinkTarget={formatLinkTarget}
    />
  );
  const searchField = (
    <FacetSearchInput
      onChange={searchInput.onChange}
      onClear={searchInput.onClear}
      placeholder={searchPlaceholder}
      value={searchInput.value}
      visible={Boolean(onSearchChange)}
    />
  );
  const desktopSort = hasSort && (
    <FilterSelect
      icon={SortIcon}
      placeholder={t("sortBy")}
      value={controller.sort}
      onChange={(value) => {
        if (value) controller.setSort(value);
      }}
      options={sortOptions}
      clearable={false}
      className="hidden sm:inline-block"
      intent="sort"
      triggerClassName="sm:min-w-[200px]"
      triggerLabelClassName="max-w-none"
    />
  );

  if (layout === "compact") {
    return (
      <FacetFilterBarCompact
        surface={surface}
        options={options}
        controller={controller}
        sortOptions={sortOptions}
        formatSelector={formatSelector}
        searchField={searchField}
        desktopSort={desktopSort}
      />
    );
  }

  return (
    <div className="space-y-3 py-3 border-b border-border mb-6">
      {(formatDef || hasSort) && (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          {formatSelector}

          {desktopSort}
        </div>
      )}

      {searchField}

      <FacetFilterControlsSection
        collapsible={collapseFiltersOnMobile}
        controller={controller}
        options={options}
        surface={surface}
      />

      <FacetMobileSort controller={controller} options={sortOptions} placeholder={t("sortBy")} />
    </div>
  );
}
